<template>
  <Loader :is-loading="isLoading">
    <div class="card">

      <div class="card card-custom" >
        <div class="card-header flex-wrap border-0 pt-6 pb-0">
          <div class="card-title">
            <h3 class="card-label">{{ $t('userGroup.list') }}</h3>
          </div>
          <div class="card-toolbar">
            <Button  btn-class="btn-sm me-3" type="new" id="submit_btn" btn-text="userGroup.new" @click.prevent="openCreateModal"></Button>
          </div>

        </div>

        <div class="card-body h-500px">
          <div class="row h-100">
            <div class="col-4 border-end border-gray-200 h-100">
              <Card class="p-0">
                <template #cardHeader>
                  <Input
                      container-class="me-2 mb-2 mb-md-0"
                      inputClass="form-control form-control-sm"
                      name="input-search"
                      :isInputGroup="true"
                      :placeholder="$t('label.search')"
                      v-model="filter.search"
                  >
                    <template #rightAddon>
                    <span class="position-absolute border-0 bg-transparent end-0 top-50 translate-middle">
                      <i class="fa fa-search"></i>
                    </span>
                    </template>
                  </Input>
                </template>
                <div v-if="!filteredGroups.length && !isLoading" class="card-body mt-5 p-0">
                  <div class="d-flex flex-column mh-500px scroll-y p-2">
                    <div class="mb-10 mt-20 text-center">
                      <div class="fs-2 fw-bold text-gray-800 text-center mb-13">
                        {{ $t('userGroup.noGroupFound') }}
                      </div>
                    </div>
                  </div>
                </div>
                <div v-else class="card-body mt-5 p-0">
                  <div class="d-flex flex-column mh-500px scroll-y p-2">
                    <Card v-for="group in filteredGroups"
                          class="card card-custom shadow-sm p-0 border  border-hover-primary mb-2 cursor-pointer"
                          :class="{'border-primary': selectedGroup === group.id, 'border-secondary': selectedGroup !== group.id}"
                          :key="group.id"
                          @click.prevent="toggleSelectedGroup(group.id)"
                    >
                      <div class="card-body">
                        <h4 class="text-gray-600"> {{ group.name }} </h4>
                      </div>
                    </Card>
                  </div>
                </div>
              </Card>
            </div>
            <div class="col-8 h-100">
              <div v-if="!selectedGroup && !isLoading"  class="card-body mt-5 p-0">
                <div class="d-flex flex-column mh-500px scroll-y p-2">
                  <div class="mb-10 mt-20 text-center">
                    <div class="fs-2 fw-bold text-gray-800 text-center mb-13">
                      {{ $t('userGroup.noGroupSelected') }}
                    </div>
                  </div>
                </div>
              </div>
              <Card v-else class="p-0 h-100">
                <template #cardHeader>
                  <div class="card-header">
                    <h3 v-if="currentGroup.name" :key="currentGroup.name" class="card-title">
                      {{ currentGroup?.name }}
                    </h3>
                    <div v-if="selectedGroup" class="card-toolbar">
                      <Button btn-class="btn-sm btn-icon me-3" type="button" icon-class="fa fa-edit" icon-color="text-gray-400" @click.prevent="openEditModal"></Button>
                      <Button btn-class="btn-sm btn-icon me-3" type="button" icon-class="fa fa-plus" icon-color="text-gray-400" @click.prevent="openAddUserModal"></Button>
                      <Button btn-class="btn-sm btn-icon" type="button" icon-class="fa fa-trash-alt" icon-color="text-gray-400" @click.prevent="deleteGroup" :is-loading="deleting"></Button>
                    </div>
                  </div>
                </template>
                <template #cardBody>
                  <div class="d-flex flex-row-fluid gap-3 flex-wrap h-75 scroll-y">
                    <Card v-for="user in currentGroup.users"
                          class="card card-custom w-md-45 w-lg-30 shadow-sm p-0 border border-secondary border-hover-primary mb-2 cursor-pointer h-100px"
                          :key="user.id"
                    >
                      <div class="card-body h-100 p-0">
                        <div class="d-flex flex-row h-100 position-relative overflow-y-hidden">
                          <div class="w-25 d-flex align-items-center justify-content-center bg-light-primary rounded-start p-3">
                            <i class="fa fa-user text-primary fs-2"></i>
                          </div>
                          <div class="w-75 d-flex flex-column justify-content-center p-3">
                            <h4 class="text-gray-800 fw-bold"> {{ user.fullname }} </h4>
                            <span class="text-gray-600 fw-bold"> {{ user.email }} </span>
                          </div>
                          <div class="position-absolute top-0 end-0 p-3 ">
                            <Button
                                btn-class="btn-sm btn-icon bg-hover-danger text-hover-white shadow-none border border-gray-200 border-hover-danger"
                                type="button"
                                icon-class="bi bi-x" icon-color="text-gray-400"
                                @click.prevent.stop="removeUser(user.id)"/>
                          </div>
                        </div>
                      </div>
                    </Card>
                  </div>
                </template>
              </Card>
            </div>
          </div>
        </div>
      </div>
      <UserGroupFormModal id="userGroupCreateModal" />
      <UserGroupFormModal id="userGroupEditModal" :is-edit="true" :user-group="formEditData" @callback="resetData"/>
      <AddMemberFormModal id="addUsersToGroupModal" :group-id="selectedGroup" :old-users="currentGroup?.users" />
    </div>
  </Loader>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { useStore } from "vuex";

import Card from "@/components/Card.vue";
import Input from "@/components/Input.vue";
import Button from "@/components/Button.vue";
import UserGroupFormModal from "@/components/UserGroup/UserGroupFormModal.vue";
import Loader from "@/components/Loader.vue";
import AddMemberFormModal from "@/components/UserGroup/AddMemberFormModal.vue";
import {useSwal} from "@/composables/useSwal";

const store = useStore();
const {showSwal} = useSwal()

const groups = computed(() => store.getters['usersGroups/getUsersGroups']);
const filteredGroups = computed(() => {
  if (!filter.value.search) return groups.value;
  return groups.value.filter(group => group.name.toLowerCase().includes(filter.value.search.toLowerCase()));
});
const currentGroup = computed(() => {
  if(!selectedGroup.value) return null
  return groups.value.find(group => group.id === selectedGroup.value)
})


const isLoading = ref(false)
const deleting = ref(false)
const filter = ref({
  search: ''
})
const selectedGroup = ref(null);
const formEditData = ref({
  id: '',
  name: '',
  ids: []
})

const openCreateModal = () => {
  $('#userGroupCreateModal').modal('toggle');
}

const openAddUserModal = () => {
  $('#addUsersToGroupModal').modal('toggle');
}

const openEditModal = () => {
  if (selectedGroup.value) {
    formEditData.value = {
      id: currentGroup.value?.id,
      name: currentGroup.value?.name,
      ids: currentGroup.value?.users?.map(user => user.id) || []
    }
    $('#userGroupEditModal').modal('toggle');
  }
}

function toggleSelectedGroup(index) {
  selectedGroup.value = selectedGroup.value === index ? null : index;
}

function deleteGroup() {
  showSwal(
      '',
      i18n.global.t('userGroup.swal.group_deleted')
  ).then( async (result) => {
    if (result.value) {
      deleting.value = true
      store.dispatch('usersGroups/deleteUserGroup', selectedGroup.value)
          .then(res => {
            selectedGroup.value = filteredGroups.value[0]?.id || ''
            window.toastr['success'](i18n.global.t('userGroup.notification.group_deleted'))
          })
          .catch()
          .finally(() => deleting.value = false)
    }
  })

}

function resetData() {
  formEditData.value = {
    id: currentGroup.value?.id,
    name: currentGroup.value?.name,
    ids: currentGroup.value?.users?.map(user => user.id) || []
  }
}

function removeUser(userId) {
  showSwal(
      '',
      i18n.global.t('userGroup.swal.userRemoved')
  ).then( async (result) => {
    if (result.value) {
      store.dispatch('usersGroups/removeUsersFromGroup',
          { group_id: selectedGroup.value, ids: [userId] })
          .then(res => {
            window.toastr['success'](i18n.global.t('userGroup.notification.userRemoved'))
          })
          .catch()
          .finally(() => {})
    }
  })

}

function loadData() {
  isLoading.value = true;
  Promise.all([
      store.dispatch('usersGroups/loadUsersGroups'),
      store.dispatch('user/loadUsers'),
  ]).finally(() => isLoading.value = false);
}

onMounted(() => {
  loadData()
});

</script>

<style scoped>

.w-md-45 {
  width: 45%;
}

.w-lg-30 {
  width: 30%;
}

@media (max-width: 1370px) {
  .w-md-45 {
    width: 45%;
  }
}

@media (max-width: 768px) {
  .w-md-45,
  .w-lg-30 {
    width: 100%;
  }
}
</style>

